import { Text, View } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import { CButton, CInput } from '@app/components/Common';
import { Button, Input, Select } from 'antd';
import BigNumber from 'bignumber.js';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SETTING_TYPE_TRADE } from '../../../../../../constants/SETTING';
import { useIndexPrice } from '../../../../../../hooks/trade/indexprice';
import useTradeStore from '../../../../../../hooks/trade/tradeStore';
import { getSetting, useSetting } from '../../../../../../hooks/user/mysetting';
import { OrderValueInputSection } from '../styles';
import { useBinanceWebSocketStore } from '../../../../../../hooks/binanceWebsocketStore';

const OrderPriceInput = ({ limit_market, isManualInputRef, isMobile }) => {
  const { textw, gray, drawerBgColor, mobileTradeBorder, textsilver } =
    React.useContext(ThemeContext).theme.COLORS;
  const {
    isDarkMode,
    theme: { COLORS },
  } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const { t } = useTranslation();
  const { data: tradeStore, mutate: mutateTradeStore } = useTradeStore();
  const { getPrice } = useIndexPrice(tradeStore?.ep_id);

  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_TRADE);
  const alarm = getSetting(setting, 'ALM' + tradeStore?.ep_id, 0);

  const [trigger, setTrigger] = React.useState('BIG');

  /**
   * ✅ 버튼 클릭 시 실시간 가격 가져오기 (항상 최신 값)
   * Zustand store에서 직접 읽어서 클릭 순간의 최신 가격 반환
   */
  const getCurrentPrice = React.useCallback(() => {
    // ✅ getState()로 클릭 순간의 최신 가격 가져오기
    const { tradePrice } = useBinanceWebSocketStore.getState();

    if (tradePrice) {
      console.log('[OrderPriceInput] 실시간 가격:', tradePrice);
      return BigNumber(tradePrice).toNumber();
    }

    // Fallback: SWR 캐시
    const fallbackPrice = getPrice(tradeStore?.ep_id);
    console.log('[OrderPriceInput] Fallback 가격:', fallbackPrice);
    return fallbackPrice;
  }, [getPrice, tradeStore?.ep_id]);

  useEffect(() => {
    if (
      tradeStore?.order_price === undefined ||
      tradeStore?.order_price === '' ||
      isNaN(tradeStore?.order_price) ||
      tradeStore?.order_price == 0
    ) {
      const price = getCurrentPrice();
      if (price) {
        mutateTradeStore({
          ...tradeStore,
          order_price: BigNumber(price).toFixed(tradeStore?.dp || 3),
        });
      }
    }
  }, [tradeStore, getCurrentPrice, mutateTradeStore]);

  const setOrderPrice = (data) => {
    if (isNaN(data)) return;
    mutateTradeStore({ ...tradeStore, order_price: data });
  };

  const setTriggerPrice = (data) => {
    if (isNaN(data)) return;
    const price = getCurrentPrice();
    const trigger = price > data ? 'BIG' : 'SML';

    mutateTradeStore({
      ...tradeStore,
      trigger_price: data,
      trigger_type: trigger,
    });
  };

  const handleTriggerButton = (type: 'plus' | 'minus') => {
    if (type === 'plus') {
      setTriggerPrice(
        BigNumber(tradeStore?.trigger_price || 0)
          .plus(1)
          .toNumber()
      );
    } else {
      setTriggerPrice(
        BigNumber(tradeStore?.trigger_price || 0)
          .minus(1)
          .toNumber()
      );
    }
  };

  return (
    <OrderValueInputSection>
      {(limit_market == 'T' ||
        limit_market == 'CL' ||
        limit_market == 'CM') && (
        <View
          style={{
            flexDirection: 'row',
            width: '100%',
            gap: 5,
            marginBottom: 10,
          }}
        >
          <Button
            style={{
              color: limit_market == 'CL' ? textw : 'silver',
              background: limit_market == 'CL' ? drawerBgColor : 'transparent',
              border: `0px`,
              borderRadius: 10,
              width: '50%',
              padding: '5px 0',
            }}
            onClick={() => {
              mutateTradeStore({
                ...tradeStore,
                limit_market: 'CL',
              });
            }}
          >
            {t('limit')}
          </Button>
          <Button
            style={{
              color: limit_market == 'CM' ? textw : 'silver',
              background: limit_market == 'CM' ? drawerBgColor : 'transparent',
              border: `0px`,
              borderRadius: 10,
              width: '50%',
              padding: '5px 0',
            }}
            onClick={() => {
              mutateTradeStore({
                ...tradeStore,
                limit_market: 'CM',
              });
            }}
          >
            {t('market')}
          </Button>
        </View>
      )}

      {limit_market != 'CM' && (
        <>
          {isMobile ? (
            <div
              style={{
                background: gray,
                color: textw,
                border: `1px solid ${mobileTradeBorder}`,
                marginTop: 5,
                padding: `4px 12px`,
                width: '100%',
                backgroundColor: theme === 'light' ? '#F8F4F0' : '#0C110E',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                alignItems: 'center',
                borderRadius: 6,
              }}
            >
              <CButton
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  setOrderPrice(
                    BigNumber(tradeStore?.order_price).minus(1).toNumber()
                  )
                }
                title={'-'}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <span style={{ fontSize: 12, color: textsilver }}>
                  {t('price')} (USDT)
                </span>
                <Input
                  value={tradeStore?.order_price}
                  onChange={(e: any) => setOrderPrice(e.target.value)}
                  style={{
                    border: 'none',
                    textAlign: 'center',
                    padding: 0,
                    fontFamily: 'Binance PLEX',
                    background: 'transparent',
                  }}
                />
              </div>
              <CButton
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  setOrderPrice(
                    BigNumber(tradeStore?.order_price).plus(1).toNumber()
                  )
                }
                title={'+'}
              />
            </div>
          ) : (
            <>
              <View style={{ flexDirection: 'row' }}>
                <Text
                  style={{
                    color: textw,
                    fontSize: 14,
                    marginBottom: 2,
                  }}
                >
                  {t('orderPrice')}
                </Text>
              </View>
              <Input
                placeholder={''}
                value={tradeStore?.order_price ? tradeStore?.order_price : null}
                type={'text'}
                onChange={(e: any) => setOrderPrice(e.target.value)}
                style={{
                  background: gray,
                  color: textw,
                  border: `1px solid ${isMobile ? mobileTradeBorder : COLORS.PrimaryInputBorder}`,
                  marginTop: 5,
                  padding: 10,
                  width: '100%',
                  backgroundColor: COLORS.PrimaryInputBg,
                  marginBottom: 10,
                  cursor: 'pointer',
                }}
                suffix={
                  <CButton
                    title={t('lastprice')}
                    onClick={() => {
                      // ✅ Realtime 서버의 실시간 가격 사용
                      const currentPrice = getCurrentPrice();
                      if (currentPrice) {
                        setOrderPrice(
                          BigNumber(currentPrice).toFixed(tradeStore?.dp || 3)
                        );
                      }
                    }}
                    style={{
                      fontSize: '12px',
                      color: COLORS.textw,
                    }}
                  />
                }
              />
            </>
          )}
        </>
      )}

      {(limit_market == 'T' || limit_market == 'CM' || limit_market == 'CL') &&
        (isMobile ? (
          <div
            style={{
              background: gray,
              color: textw,
              border: `1px solid ${mobileTradeBorder}`,
              padding: `4px 12px`,
              width: '100%',
              backgroundColor: theme === 'light' ? '#F8F4F0' : '#0C110E',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              alignItems: 'center',
              borderRadius: 6,
              marginTop: 15,
            }}
          >
            <CButton onClick={() => handleTriggerButton('minus')} title={'-'} />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                alignItems: 'center',
                flex: 1,
              }}
            >
              <span style={{ fontSize: 12, color: textsilver }}>
                {t('triggerPrice')} (USDT)
              </span>
              <Input
                value={
                  tradeStore?.trigger_price ? tradeStore?.trigger_price : 0
                }
                type={'text'}
                onChange={(e: any) => setTriggerPrice(e.target.value)}
                style={{
                  border: 'none',
                  textAlign: 'center',
                  padding: 0,
                  fontFamily: 'Binance PLEX',
                  background: 'transparent',
                }}
              />
            </div>
            <CButton onClick={() => handleTriggerButton('plus')} title={'+'} />
          </div>
        ) : (
          <>
            <Text
              style={{
                color: textw,
                fontSize: 13,
                marginBottom: 3,
              }}
            >
              {t('triggerPrice')}
            </Text>
            <Input
              placeholder={''}
              value={
                tradeStore?.trigger_price ? tradeStore?.trigger_price : null
              }
              type={'text'}
              onChange={(e: any) => setTriggerPrice(e.target.value)}
              style={{
                background: gray,
                color: textw,
                border: `1px solid ${isMobile ? mobileTradeBorder : COLORS.PrimaryInputBorder}`,
                padding: 10,
                width: '100%',
                backgroundColor: COLORS.PrimaryInputBg,
              }}
            />
          </>
        ))}
    </OrderValueInputSection>
  );
};

export default React.memo(OrderPriceInput);
